/**
 * Extracts the year from a date using local time components.
 * This avoids timezone conversion issues where a date like "2026-01-01"
 * might be converted to UTC and show as 2025-12-31.
 *
 * The key is to use getFullYear(), getMonth(), and getDate() which always
 * return local time values, regardless of how the Date object was created.
 */
export function getLocalYear(date: Date): number {
  // getFullYear() always returns the local year, not UTC
  // This is safe even if the Date was created from an ISO string
  return date.getFullYear();
}

/**
 * Creates a date at midnight in local timezone from year, month, day.
 * This ensures the date represents the intended calendar date without timezone shifts.
 */
export function createLocalDate(
  year: number,
  month: number,
  day: number
): Date {
  // Create date using local time constructor (not UTC)
  // month is 0-indexed in JavaScript Date constructor
  return new Date(year, month - 1, day);
}

/**
 * Extracts year, month, day from a Date object using local time components.
 * Returns an object with the local date components.
 */
export function getLocalDateComponents(date: Date): {
  year: number;
  month: number;
  day: number;
} {
  return {
    year: date.getFullYear(),
    month: date.getMonth() + 1, // getMonth() returns 0-11, we want 1-12
    day: date.getDate(),
  };
}

/**
 * Maps app locale codes to Intl.DateTimeFormat locale strings.
 */
const LOCALE_MAP: Record<string, string> = {
  en: 'en-US',
  sr: 'sr-Latn-RS',
  si: 'sl-SI',
};

/**
 * Formats a date for display using local timezone to avoid timezone shifts.
 * When parsing date strings like "2026-01-01", uses local time constructor
 * instead of UTC to preserve the intended calendar date.
 *
 * When a locale is provided, the date is formatted using the native
 * Intl.DateTimeFormat for that locale (e.g. "09. mar. 2026" for Slovenian).
 */
export function getDateFormat(
  input: Date | string | null | undefined,
  locale?: string,
  timeZone: string = 'Europe/Ljubljana'
): string {
  if (!input) return '';

  let d: Date;
  if (typeof input === 'string') {
    const s = input.trim();

    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
      // Parse YYYY-MM-DD format using local time to avoid timezone shifts
      const [y, m, day] = s.split('-').map(Number);
      d = new Date(y, m - 1, day);
    } else {
      d = new Date(s);
    }
  } else {
    d = input;
  }

  if (isNaN(d.getTime())) return '';

  const intlLocale = (locale && LOCALE_MAP[locale]) || 'en-US';

  return new Intl.DateTimeFormat(intlLocale, {
    timeZone,
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  }).format(d);
}

/**
 * Formats a date with time for display (e.g. "Mar 9, 2026, 2:30 PM").
 */
export function getDateTimeFormat(
  input: Date | string | null | undefined,
  locale?: string,
  timeZone: string = 'Europe/Ljubljana'
): string {
  if (!input) return '';

  let d: Date;
  if (typeof input === 'string') {
    d = new Date(input);
  } else {
    d = input;
  }

  if (isNaN(d.getTime())) return '';

  const intlLocale = (locale && LOCALE_MAP[locale]) || 'en-US';

  return new Intl.DateTimeFormat(intlLocale, {
    timeZone,
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
}

/**
 * Formats a Date object for HTML date input (type="date") using local timezone.
 * Returns YYYY-MM-DD format without timezone conversion to preserve the calendar date.
 *
 * @example
 * formatDateForInput(new Date(2026, 0, 1)) // Returns "2026-01-01"
 */
export function formatDateForInput(date: Date | null | undefined): string {
  if (!date || isNaN(date.getTime())) return '';

  // Use local date components to avoid timezone shifts
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}
